import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

/**
 * Data hooks for the Proposals feature.
 *
 * - The proposal itself is Ops-owned (`/api/proposals` CRUD).
 * - The CATALOG it's built from — items, packages, discounts, price-metrics,
 *   closers — is proxied live from the Cinch billing API in one `/catalog`
 *   payload. Cinch uses `price_metric` where the source app used
 *   `unit_economic_id`, and `is_archived` instead of `is_active`.
 */

// ── Cinch catalog row shapes (only the fields the builder reads) ─────────────
export interface CatalogItem {
  id: string;
  name: string;
  sku_cinch?: string | null;
  category?: string | null;
  price_metric?: string | null;
  price_metric_name?: string | null;
  unit_cost: number;
  floor_price: number;
  starting_price: number;
  recommended_price: number;
  is_archived?: boolean;
}

export interface PackageItemRef {
  item?: string | null;
  quantity_limit?: number | null;
  included_price?: number | null;
}

export interface CatalogPackage {
  id: string;
  name: string;
  sku?: string | null;
  descr?: string | null;
  price: number;
  estimated_gross_margin: number;
  pricing_type: string; // "flat_fee" | "per_unit"
  proposal_eligible: boolean;
  price_metric?: string | null;
  price_metric_name?: string | null;
  package_items?: PackageItemRef[];
  is_archived?: boolean;
}

export interface UnitGrant {
  price_metric: string | null;
  quantity: number;
  recurrence: string; // "monthly" | "one_time"
}

export type DiscountType =
  | "percentage"
  | "fixed"
  | "tiered"
  | "time_based"
  | "quantity_based";

export interface CatalogDiscount {
  id: string;
  name: string;
  description?: string | null;
  type: DiscountType;
  value: number | null;
  code?: string | null;
  stackable: boolean;
  applicable_items?: string[];
  applicable_packages?: string[];
  min_quantity?: number | null;
  max_quantity?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  duration_periods?: number | null;
  period_unit?: string | null;
  unit_grants?: UnitGrant[];
  is_archived?: boolean;
}

export interface PriceMetric {
  id: string;
  name: string;
}

export interface Closer {
  id: string;
  name: string;
  email?: string | null;
  hubspot_owner_id?: string | null;
}

export interface Catalog {
  items: CatalogItem[];
  packages: CatalogPackage[];
  discounts: CatalogDiscount[];
  price_metrics: PriceMetric[];
  closers: Closer[];
}

// ── Saved proposal shapes (mirror backend schemas.ProposalOut) ───────────────
/** A customer-facing package: a named group of lines shown as one line. Shaped
 *  to mirror a Cinch billing package (contracts `PackageRow`) so it maps 1:1 on a
 *  future Cinch-package push. `price` null = auto-sum the member lines. */
export interface ProposalBundle {
  id: string;
  name: string;
  sku: string | null;
  descr: string | null;
  customer_facing_descr: string | null;
  pricing_type: string; // "flat_fee" | "per_unit"
  price: number | null;
  price_metric: string | null;
}

export interface ProposalLineItem {
  id: string;
  bundle_id: string | null;
  item_id: string | null;
  package_id: string | null;
  quantity: number;
  display_as_unlimited: boolean;
  unit_price: number;
  subtotal: number;
  gross_margin: number;
}

export interface ProposalDiscountUnitOverride {
  price_metric_id: string;
  quantity: number;
}

export interface ProposalDiscount {
  id: string;
  discount_id: string;
  target_line_item_id: string | null;
  duration_override: number | null;
  unit_overrides: ProposalDiscountUnitOverride[];
}

export type RenewalTerm = "SAME_TERM" | "DIFFERENT_TERM" | "MONTH_TO_MONTH";

export interface Proposal {
  id: string;
  name: string;
  customer_name: string;
  contract_months: number;
  closer_id: string | null;
  closer_name: string | null;
  locations: number | null;
  is_auto_renew: boolean;
  renewal_term: RenewalTerm | null;
  renewal_term_months: number | null;
  mtm_uplift_pct: number | null;
  billing_effective_from: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  bundles: ProposalBundle[];
  line_items: ProposalLineItem[];
  discounts: ProposalDiscount[];
}

// ── Write payload (mirror backend schemas.ProposalIn) ────────────────────────
export interface ProposalBundleIn {
  local_id: string;
  name: string;
  sku: string | null;
  descr: string | null;
  customer_facing_descr: string | null;
  pricing_type: string;
  price: number | null;
  price_metric: string | null;
}

export interface ProposalLineItemIn {
  local_id: string;
  bundle_local_id: string | null;
  item_id: string | null;
  package_id: string | null;
  quantity: number;
  display_as_unlimited: boolean;
  unit_price: number;
  subtotal: number;
  gross_margin: number;
}

export interface ProposalDiscountIn {
  discount_id: string;
  target_line_local_id: string | null;
  duration_override: number | null;
  unit_overrides: ProposalDiscountUnitOverride[];
}

export interface ProposalIn {
  name: string;
  customer_name: string;
  contract_months: number;
  closer_id: string | null;
  closer_name: string | null;
  locations: number | null;
  is_auto_renew: boolean;
  renewal_term: RenewalTerm | null;
  renewal_term_months: number | null;
  mtm_uplift_pct: number | null;
  billing_effective_from: string | null;
  notes: string | null;
  bundles: ProposalBundleIn[];
  line_items: ProposalLineItemIn[];
  discounts: ProposalDiscountIn[];
}

// ── Hooks ────────────────────────────────────────────────────────────────────
export function useCatalog() {
  return useQuery({
    queryKey: ["proposals", "catalog"],
    queryFn: () => apiFetch<Catalog>("/proposals/catalog"),
    staleTime: 5 * 60_000, // catalog changes rarely; don't refetch on every mount
  });
}

export function useProposals() {
  return useQuery({
    queryKey: ["proposals", "list"],
    queryFn: () => apiFetch<Proposal[]>("/proposals"),
  });
}

export function useCreateProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProposalIn) =>
      apiFetch<Proposal>("/proposals", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals", "list"] }),
  });
}

export function useUpdateProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ProposalIn }) =>
      apiFetch<Proposal>(`/proposals/${id}`, { method: "PUT", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals", "list"] }),
  });
}

export function useDeleteProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/proposals/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals", "list"] }),
  });
}
